import { Box, Grid, Typography } from "@mui/material";
import {
    AISpeakerHighlight,
    EvalStore,
} from "components/Evaluation/Stores/EvalStore";
import AcxToast from "components/UI/AcxToast";
import { observer } from "mobx-react";
import { Answer } from "models/Answer";
import { PredictedTag } from "models/PredictedTag";
import moment from "moment";
import React, { FunctionComponent } from "react";
import { useStore } from "utils/useStore";
import { AIExplanationNoHighlights } from "./AIExplanationNoHighlights";

interface OwnProps {
    tagId: string;
    predictedTags: PredictedTag[];
    answer: Answer;
}

type Props = OwnProps;

const AIExplanationContext: FunctionComponent<Props> = observer(
    (props: Props) => {
        const store = useStore(EvalStore);

        const renderHighlights = (
            highlight: AISpeakerHighlight,
            formattedHighlightString: string,
            index,
        ) => {
            return (
                <Grid
                    key={"speaker-highlight" + index + props.tagId}
                    item
                    container
                    xs={12}
                    wrap="nowrap"
                    sx={{ minHeight: "82px" }}
                    className={"pendo-ignore"}
                >
                    <Grid
                        container
                        item
                        xs={3}
                        direction="column"
                        alignContent="flex-start"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                        sx={{
                            gap: "10px",
                            flexShrink: 0,
                            display: "flex",
                            paddingRight: "2px",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            maxWidth: "85px",
                        }}
                    >
                        <Grid
                            sx={{
                                padding: "12px 10px",
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                                flexDirection: "column",
                                maxWidth: "90px",
                            }}
                        >
                            <Typography
                                sx={{ fontWeight: 700, textWrap: "nowrap" }}
                                style={{
                                    color: highlight.color,
                                }}
                            >
                                {highlight.speaker}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box
                        style={{
                            width: "2px",
                            height: "82%",
                            backgroundColor: highlight.color,
                            margin: "auto 0",
                        }}
                    />
                    <Grid
                        item
                        xs={9}
                        sx={{
                            padding: "10px 39px 14px 12px",
                            gap: "8px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            width: "100%",
                        }}
                    >
                        <Typography>
                            {" "}
                            <span
                                className={"pendo-ignore"}
                                dangerouslySetInnerHTML={{
                                    __html: formattedHighlightString,
                                }}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            );
        };

        const modifiedPredictorsString = props.predictedTags
            .filter(
                (tag) =>
                    store.structuredHighlights.has(tag.tagId) &&
                    moment(
                        store.structuredHighlights.get(tag.tagId)
                            ?.predictorModifiedOn,
                    ).isAfter(props.answer.modifiedOn),
            )
            .map(
                (tag) =>
                    `"${
                        store.structuredHighlights.get(tag.tagId)!.predictorName
                    }"`,
            )
            .join(" and ");

        const isEmpty =
            !store.structuredHighlights.has(props.tagId) ||
            !store.structuredHighlights.get(props.tagId)?.aiSpeakerHighlights
                ?.length;

        const structuredHighlight = store.structuredHighlights.get(props.tagId);

        return (
            store.authStore.canUserView("Transcription") && (
                <Grid
                    container
                    direction="row"
                    alignItems="flex-start"
                    sx={{
                        width: "100%",
                        padding: "10px",
                        gap: "10px",
                        backgroundColor: (t) => t.palette.neutralGray.main,
                    }}
                    wrap="nowrap"
                    key={"context-container" + props.tagId}
                >
                    <Grid
                        sx={{
                            padding: "0px!important",
                            backgroundColor: (t) => t.palette.neutralGray.main,
                            width: "100%",
                        }}
                        key={"card-content" + props.tagId}
                    >
                        {!!modifiedPredictorsString && (
                            <AcxToast
                                message={`A user changed the definition of ${modifiedPredictorsString} after scoring was complete. The information in the transcription may no longer reflect this score.`}
                                title={""}
                                fullWidth
                                fullWidthMessage
                                severity="infoV2"
                                wrapText
                            />
                        )}
                        {isEmpty && <AIExplanationNoHighlights />}
                        {!isEmpty &&
                            structuredHighlight &&
                            structuredHighlight.aiSpeakerHighlights.map(
                                (highlight, index) => {
                                    let formattedHighlightString: string =
                                        highlight.highlightText
                                            .replaceAll(
                                                "<b>",
                                                `<span style="color:#3564D5;font-weight:700;">`,
                                            )
                                            .replaceAll("</b>", "</span>");

                                    return (
                                        <Grid
                                            container
                                            xs={12}
                                            item
                                            direction="row"
                                            alignContent="flex-start"
                                            className={"pendo-ignore"}
                                            key={
                                                "spkr-highlight-" +
                                                props.tagId +
                                                index
                                            }
                                        >
                                            {renderHighlights(
                                                highlight,
                                                formattedHighlightString,
                                                index,
                                            )}
                                        </Grid>
                                    );
                                },
                            )}
                    </Grid>
                </Grid>
            )
        );
    },
);
export default AIExplanationContext;
